import React, { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import Scene from "@/components/Scene";
import SongPlayer from "@/components/SongPlayer";

const CHAPTERS = [
  {
    id: 0,
    caption:
      "She walked into the frame, carrying the whole world on her shoulders",
  },
  {
    id: 1,
    caption: "The rain wouldn't stop. Nothing felt right anymore.",
  },
  {
    id: 2,
    caption:
      "She sat there with her roses. Beautiful things in a broken moment.",
  },
  {
    id: 3,
    caption: "But storms don't last forever, Hetvi. They never do.",
  },
  {
    id: 4,
    caption: "Slowly, the sky remembered it owes you a sunrise.",
  },
  {
    id: 5,
    caption:
      "She smiled. And the whole world bloomed right alongside her 🌹",
  },
  {
    id: 6,
    caption: null,
  },
];

const TRANSITION_MS = 900;
const WHEEL_SILENCE_MS = 200;

export default function App() {
  const [chapter, setChapter] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  const chapterRef = useRef(0);
  const lockRef = useRef(false);
  const lastInputRef = useRef(0);
  const transitionTimerRef = useRef(null);
  const releaseTimerRef = useRef(null);
  const touchStartYRef = useRef(null);

  const goTo = useCallback((target) => {
    if (lockRef.current) return;
    const cur = chapterRef.current;
    const next = Math.max(0, Math.min(CHAPTERS.length - 1, target));
    if (next === cur) return;
    lockRef.current = true;
    chapterRef.current = next;
    setChapter(next);

    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);

    const tryRelease = () => {
      const since = Date.now() - lastInputRef.current;
      if (since >= WHEEL_SILENCE_MS) {
        lockRef.current = false;
      } else {
        releaseTimerRef.current = setTimeout(
          tryRelease,
          WHEEL_SILENCE_MS - since + 20
        );
      }
    };

    transitionTimerRef.current = setTimeout(tryRelease, TRANSITION_MS);
  }, []);

  const advance = useCallback(
    (dir) => {
      goTo(chapterRef.current + dir);
    },
    [goTo]
  );

  // Attach listeners once
  useEffect(() => {
    const onWheel = (e) => {
      e.preventDefault();
      lastInputRef.current = Date.now();
      if (lockRef.current) return;
      if (Math.abs(e.deltaY) < 6) return;
      advance(e.deltaY > 0 ? 1 : -1);
    };
    const onKey = (e) => {
      if (
        ["ArrowDown", "ArrowRight", "PageDown", " ", "Enter"].includes(e.key)
      ) {
        e.preventDefault();
        lastInputRef.current = Date.now();
        advance(1);
      } else if (["ArrowUp", "ArrowLeft", "PageUp"].includes(e.key)) {
        e.preventDefault();
        lastInputRef.current = Date.now();
        advance(-1);
      } else if (e.key === "Home") {
        goTo(0);
      } else if (e.key === "End") {
        goTo(CHAPTERS.length - 1);
      }
    };
    const onTouchStart = (e) => {
      touchStartYRef.current = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      if (touchStartYRef.current == null) return;
      const dy = touchStartYRef.current - e.changedTouches[0].clientY;
      if (Math.abs(dy) > 30) {
        lastInputRef.current = Date.now();
        advance(dy > 0 ? 1 : -1);
      }
      touchStartYRef.current = null;
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [advance, goTo]);

  // Unmount cleanup for timers
  useEffect(
    () => () => {
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
      if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    },
    []
  );

  // Mobile / small-screen detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  const progress = (chapter / (CHAPTERS.length - 1)) * 100;

  if (isMobile) {
    return (
      <div className="mobile-gate" data-testid="mobile-gate">
        <div className="mg-grain" aria-hidden />
        <div className="mg-vignette" aria-hidden />
        <div className="mg-inner">
          <div className="mg-rose" aria-hidden>🌹</div>
          <h1 className="mg-title">
            I told you to open it on your laptop&nbsp;
            <span className="mg-name">Hetuuuuuuu</span>
          </h1>
          <div className="mg-sub">Yug</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fh-root" data-testid="for-hetvi-root">
      <div className="top-progress" aria-hidden>
        <div
          className="top-progress-fill"
          style={{ width: `${progress}%` }}
          data-testid="top-progress-fill"
        />
      </div>

      <Scene chapter={chapter} />

      <div className="caption-wrap" aria-live="polite">
        {CHAPTERS.map((c) =>
          c.caption ? (
            <div
              key={c.id}
              className={`caption ${
                chapter === c.id
                  ? "caption-active"
                  : chapter > c.id
                  ? "caption-past"
                  : "caption-future"
              }`}
              data-testid={`caption-${c.id}`}
            >
              {c.caption}
            </div>
          ) : null
        )}
      </div>

      <div
        className={`final-card-wrap ${chapter === 6 ? "on" : "off"}`}
        data-testid="final-card"
      >
        <div className="final-card">
          <div className="final-title">
            I know you are very sad right now, and I know this wasn't
            necessary. But it felt like I had to.
          </div>
          <div className="final-body">
            I can't see you sad like this. Not you. You deserve every
            beautiful thing this world holds. Through every storm and every
            hard day, I'm here. You can call me, text me, anytime. I will be
            there, standing for you. Always.
          </div>
          <div className="final-red-string">
            <span className="thread-l" aria-hidden />
            <span className="thread-text">
              I hope I am your red string&nbsp;theory.
            </span>
            <span className="thread-r" aria-hidden />
          </div>
          <div className="final-sig">
            <span className="sig-name">Yug Soni</span>
            <span className="sig-sub">always in your corner 🌹</span>
          </div>
        </div>
      </div>

      <nav className="dot-nav" aria-label="Chapters" data-testid="dot-nav">
        {CHAPTERS.map((c, i) => (
          <button
            key={c.id}
            type="button"
            onClick={() => goTo(i)}
            className={`dot ${chapter === i ? "dot-active" : ""}`}
            aria-label={`Go to chapter ${i + 1}`}
            data-testid={`dot-${i}`}
          />
        ))}
      </nav>

      <div
        className={`scroll-hint ${chapter === 0 ? "on" : "off"}`}
        data-testid="scroll-hint"
      >
        <span>scroll · swipe · arrow keys</span>
        <div className="hint-arrow">↓</div>
      </div>

      <SongPlayer src={`${process.env.PUBLIC_URL || ""}/song.mp3`} />
    </div>
  );
}
